import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useMatch } from 'react-router-dom'

import { fetchChatMessages, useChatPolling, type ChatMessage } from '../api/chat'
import { listChatPartners, type ChatPartner } from '../api/partners'
import type { ChatPartnerSelection, UserRole } from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { devCounterpartyOrgIdForRole, isDevMode } from '../config/devMode'
import { MarketEmptyState } from '../components/MarketEmptyState'
import { MarketPageHeader } from '../components/MarketPageHeader'

type ChatViewProps = {
  organizationId: number
  role: UserRole
  active: boolean
  partner: ChatPartnerSelection | null
  onSelectPartner: (partner: ChatPartnerSelection | null) => void
}

type MessageGroup = {
  dateLabel: string
  messages: ChatMessage[]
}

const counterpartyLabel = (role: UserRole): string =>
  role === 'supplier' ? 'рестораном' : 'поставщиком'

const initialsFromName = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) {
    return '?'
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase()
  }
  return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase()
}

const formatMessageTime = (iso: string): string => {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return ''
  }
  return date.toLocaleString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
  })
}

const formatDateLabel = (iso: string): string => {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return ''
  }
  const today = new Date()
  const yesterday = new Date()
  yesterday.setDate(today.getDate() - 1)

  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate()

  if (sameDay(date, today)) {
    return 'Сегодня'
  }
  if (sameDay(date, yesterday)) {
    return 'Вчера'
  }
  return date.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
  })
}

type ChatPreview = {
  body: string
  createdAt: string
}

const truncatePreview = (body: string, maxLength = 72): string => {
  const normalized = body.trim().replace(/\s+/g, ' ')
  if (normalized.length <= maxLength) {
    return normalized
  }
  return `${normalized.slice(0, maxLength - 1)}…`
}

const latestMessage = (messages: ChatMessage[]): ChatMessage | null =>
  messages.length > 0 ? messages[messages.length - 1] : null

const groupMessagesByDate = (messages: ChatMessage[]): MessageGroup[] => {
  const groups: MessageGroup[] = []
  for (const message of messages) {
    const dateLabel = formatDateLabel(message.createdAt)
    const last = groups[groups.length - 1]
    if (last?.dateLabel === dateLabel) {
      last.messages.push(message)
    } else {
      groups.push({ dateLabel, messages: [message] })
    }
  }
  return groups
}

const AttachIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"
    />
  </svg>
)

const SendIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M3.4 20.6 21 12 3.4 3.4l2.8 7.2L16 12l-9.8 1.4-2.8 7.2z" />
  </svg>
)

export const ChatView = ({
  organizationId,
  role,
  active,
  partner,
  onSelectPartner,
}: ChatViewProps) => {
  const [draft, setDraft] = useState('')
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [fileNotice, setFileNotice] = useState<string | null>(null)
  const [partners, setPartners] = useState<ChatPartner[]>([])
  const [partnerPreviews, setPartnerPreviews] = useState<Record<number, ChatPreview>>({})
  const [partnersLoading, setPartnersLoading] = useState(false)
  const [partnersError, setPartnersError] = useState<string | null>(null)
  const listRef = useRef<HTMLDivElement | null>(null)
  const messagesEndRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLTextAreaElement | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const devPartnerAutoSelectedRef = useRef(false)

  const { messages, loading, error, sending, handleSend, isOwnMessage } = useChatPolling(
    organizationId,
    partner?.organizationId ?? null,
    active && partner != null,
  )

  const messageGroups = useMemo(() => groupMessagesByDate(messages), [messages])

  useEffect(() => {
    const loadPartners = async () => {
      setPartnersLoading(true)
      setPartnersError(null)
      try {
        const items = await listChatPartners(organizationId, role)
        setPartners(items)
      } catch (loadError) {
        setPartnersError(loadError instanceof Error ? loadError.message : 'Не удалось загрузить контакты')
      } finally {
        setPartnersLoading(false)
      }
    }

    void loadPartners()
  }, [organizationId, role])

  useEffect(() => {
    if (partners.length === 0) {
      setPartnerPreviews({})
      return
    }

    let cancelled = false
    void (async () => {
      const entries = await Promise.all(
        partners.map(async (item) => {
          try {
            const response = await fetchChatMessages(organizationId, item.organizationId, undefined, 50)
            const message = latestMessage(response.items)
            if (!message) {
              return null
            }
            return [
              item.organizationId,
              { body: truncatePreview(message.body), createdAt: message.createdAt },
            ] as const
          } catch {
            return null
          }
        }),
      )

      if (cancelled) {
        return
      }

      setPartnerPreviews(
        Object.fromEntries(
          entries.filter((entry): entry is readonly [number, ChatPreview] => entry != null),
        ),
      )
    })()

    return () => {
      cancelled = true
    }
  }, [organizationId, partners])

  useEffect(() => {
    if (!isDevMode || partner != null || devPartnerAutoSelectedRef.current) {
      return
    }
    const counterpartyOrgId = devCounterpartyOrgIdForRole(role)
    if (!counterpartyOrgId) {
      return
    }
    devPartnerAutoSelectedRef.current = true
    onSelectPartner({
      organizationId: counterpartyOrgId,
      name: role === 'supplier' ? 'Ресторан (dev)' : 'Поставщик (dev)',
    })
  }, [onSelectPartner, partner, role])

  useEffect(() => {
    if (partner == null) {
      return
    }
    const scrollToBottom = () => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' })
    }
    requestAnimationFrame(scrollToBottom)
  }, [messages, partner, loading])

  const handleInput = (value: string) => {
    setDraft(value)
    const node = inputRef.current
    if (!node) {
      return
    }
    node.style.height = 'auto'
    node.style.height = `${Math.min(node.scrollHeight, 100)}px`
  }

  const handleFilePick = (file: File | null) => {
    setFileNotice(null)
    if (!file) {
      setPendingFile(null)
      return
    }
    setPendingFile(file)
    setFileNotice('Файлы пока не отправляются — скоро добавим. Можно написать текст.')
  }

  const handleSubmit = async () => {
    if (pendingFile && !draft.trim()) {
      setFileNotice('Сначала отправка файлов не поддерживается. Напишите текст или уберите вложение.')
      return
    }

    const sent = await handleSend(draft)
    if (sent) {
      setDraft('')
      setPendingFile(null)
      setFileNotice(null)
      if (inputRef.current) {
        inputRef.current.style.height = ''
      }
    }
  }

  const inboxPanel = (
    <>
      {partnersLoading ? <p className="echat-inbox__status">Загрузка…</p> : null}
      {partnersError ? <p className="echat-error">{partnersError}</p> : null}
      {!partnersLoading && partners.length === 0 ? (
        <MarketEmptyState
          title="Нет чатов"
          description={`Контакты появятся после первой заявки с ${counterpartyLabel(role)} или откройте чат из карточки заявки.`}
          actions={[{ label: 'К заявкам', to: '/orders', variant: 'ghost' }]}
        />
      ) : null}
      {partners.length > 0 ? (
        <div className="echat-inbox">
          <ul className="echat-inbox__list">
            {partners.map((item) => {
              const isActive = partner?.organizationId === item.organizationId
              const preview = partnerPreviews[item.organizationId]
              return (
                <li key={item.organizationId} className="echat-inbox__item">
                  <button
                    type="button"
                    className={
                      isActive ? 'echat-inbox__row echat-inbox__row--active' : 'echat-inbox__row'
                    }
                    onClick={() =>
                      onSelectPartner({
                        organizationId: item.organizationId,
                        name: item.name,
                      })
                    }
                  >
                    <span className="echat-inbox__avatar" aria-hidden="true">
                      {initialsFromName(item.name)}
                    </span>
                    <span className="echat-inbox__body">
                      <span className="echat-inbox__name">{item.name}</span>
                      <span className="echat-inbox__preview">
                        {preview ? (
                          <>
                            {preview.body}
                            <span className="echat-muted"> · {formatMessageTime(preview.createdAt)}</span>
                          </>
                        ) : (
                          'Нажмите, чтобы открыть чат'
                        )}
                      </span>
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      ) : null}
    </>
  )

  const threadPanel = partner ? (
    <section aria-label={`Чат с ${partner.name}`} className="echat-thread">
      <header className="echat-header">
        <button
          type="button"
          className="echat-header__back"
          aria-label="К списку чатов"
          onClick={() => onSelectPartner(null)}
        >
          ‹
        </button>
        <span className="echat-header__avatar" aria-hidden="true">
          {initialsFromName(partner.name)}
        </span>
        <div className="echat-header__info">
          <h2 className="echat-header__name">{partner.name}</h2>
          <p className="echat-header__status">
            <span className="echat-header__status-dot" aria-hidden="true" />
            в сети
          </p>
        </div>
      </header>

      <div ref={listRef} className="echat-messages" aria-live="polite">
        {loading ? <p className="echat-messages__status">Загрузка…</p> : null}
        {!loading && messages.length === 0 ? (
          <p className="echat-messages__status">Напишите первое сообщение</p>
        ) : null}
        {messageGroups.map((group) => (
          <div key={group.dateLabel} className="echat-day">
            <p className="echat-day__label">{group.dateLabel}</p>
            {group.messages.map((message) => {
              const own = isOwnMessage(message)
              return (
                <div
                  key={message.id}
                  className={own ? 'echat-bubble echat-bubble--out' : 'echat-bubble echat-bubble--in'}
                >
                  <p className="echat-bubble__text">{message.body}</p>
                  <time className="echat-bubble__time" dateTime={message.createdAt}>
                    {formatMessageTime(message.createdAt)}
                  </time>
                </div>
              )
            })}
          </div>
        ))}
        <div ref={messagesEndRef} className="echat-messages__anchor" aria-hidden="true" />
      </div>

      <div className="echat-thread-dock">
        {error ? <p className="echat-error echat-thread-dock__error">{error}</p> : null}
        {fileNotice ? <p className="echat-error echat-thread-dock__error">{fileNotice}</p> : null}

        {pendingFile ? (
          <div className="echat-attach-preview">
            <span className="echat-attach-preview__name">📎 {pendingFile.name}</span>
            <button
              type="button"
              className="echat-attach-preview__remove"
              aria-label="Убрать файл"
              onClick={() => {
                setPendingFile(null)
                setFileNotice(null)
              }}
            >
              ×
            </button>
          </div>
        ) : null}

        <form
          className="echat-compose"
          onSubmit={(event) => {
            event.preventDefault()
            void handleSubmit()
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            className="visually-hidden"
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
            onChange={(event) => handleFilePick(event.target.files?.[0] ?? null)}
          />
          <button
            type="button"
            className="echat-compose__attach"
            aria-label="Прикрепить файл"
            onClick={() => fileInputRef.current?.click()}
          >
            <AttachIcon />
          </button>
          <div className="echat-compose__field">
            <label className="visually-hidden" htmlFor="echat-message-input">
              Сообщение
            </label>
            <textarea
              id="echat-message-input"
              ref={inputRef}
              className="echat-compose__input"
              rows={1}
              value={draft}
              disabled={sending}
              placeholder="Сообщение…"
              onChange={(event) => handleInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault()
                  void handleSubmit()
                }
              }}
            />
          </div>
          <button
            type="submit"
            className="echat-compose__send"
            disabled={sending || !draft.trim()}
            aria-label={sending ? 'Отправка' : 'Отправить'}
          >
            <SendIcon />
          </button>
        </form>
      </div>
    </section>
  ) : (
    <MarketEmptyState
      title="Выберите чат"
      description="Контакт слева или откройте чат из карточки заявки"
    />
  )

  return (
    <div
      className={`market-chat market-chat-split${partner ? ' market-chat-split--thread-open' : ''}`}
      aria-label="Чаты"
    >
      <aside className="market-chat-split__inbox">{inboxPanel}</aside>
      <main className="market-chat-split__thread">{threadPanel}</main>
    </div>
  )
}

type ChatLocationState = {
  partnerId?: number
  partnerName?: string
}

export const ChatScreen = () => {
  const { session, role, loading } = useAuth()
  const location = useLocation()
  const isChatRoute = useMatch('/chat') != null
  const [partner, setPartner] = useState<ChatPartnerSelection | null>(null)

  useEffect(() => {
    const state = location.state as ChatLocationState | null
    if (state?.partnerId && state.partnerName) {
      setPartner({ organizationId: state.partnerId, name: state.partnerName })
    }
  }, [location.state])

  if (loading || !session || !role) {
    return <p className="echat-inbox__status">Загрузка…</p>
  }

  return (
    <section className="market-page market-page--chat-split">
      <MarketPageHeader
        title="Чат"
        description="Переписка с поставщиками и ресторанами по заявкам."
        actions={
          <Link to="/orders" className="echat-btn echat-btn--ghost echat-btn--sm">
            К заявкам
          </Link>
        }
      />
      <ChatView
        organizationId={session.organizationId}
        role={role}
        active={isChatRoute}
        partner={partner}
        onSelectPartner={setPartner}
      />
    </section>
  )
}
